import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Tag,
  Popconfirm,
  Typography,
  Select,
  Switch,
  InputNumber,
  Row,
  Col,
  Alert,
  Collapse,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { replyTemplateAPI, botAPI } from '../services/api';
import InlineButtonEditor, { InlineButtonLayout } from '../components/InlineButtonEditor';
import DynamicVariableEditor, { DynamicVariableConfig } from '../components/DynamicVariableEditor';
import SubPagesEditor from '../components/SubPagesEditor';
import TelegramChatPreview, { SubPage } from '../components/TelegramChatPreview';

const { TextArea } = Input;
const { Title, Text } = Typography;

interface ReplyTemplate {
  id: number;
  bot_id: number | null;
  trigger_type: string;
  trigger_value: string;
  reply_text: string;
  reply_type: string;
  media_url: string | null;
  parse_mode: string;
  show_keyboard: number;
  inline_buttons: any;
  dynamic_variables: any;
  sub_pages: any;
  permission_level: number;
  priority: number;
  status: number;
  description: string | null;
  create_time: string;
}

interface BotConfig {
  id: number;
  bot_name: string;
  bot_username: string;
}

const PrivateReplyTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<ReplyTemplate[]>([]);
  const [bots, setBots] = useState<BotConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ReplyTemplate | null>(null);
  const [subPagesCollapseKey, setSubPagesCollapseKey] = useState<string[]>([]);
  const [filters, setFilters] = useState<{
    botId?: number | null;
    triggerType?: string;
    status?: number;
  }>({});
  const [form] = Form.useForm();

  const fetchBots = async () => {
    try {
      const response = await botAPI.getAll();
      setBots(response.data);
    } catch {
      message.error('获取机器人列表失败');
    }
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await replyTemplateAPI.getAll(filters);
      setTemplates(response.data);
    } catch {
      message.error('获取回复模板失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBots(); }, []);
  useEffect(() => { fetchTemplates(); }, [filters]);

  const openModal = (template?: ReplyTemplate) => {
    if (template) {
      setEditingTemplate(template);

      let dynamicVariables: DynamicVariableConfig | null = null;
      if (template.dynamic_variables) {
        try {
          dynamicVariables = typeof template.dynamic_variables === 'string'
            ? JSON.parse(template.dynamic_variables)
            : template.dynamic_variables;
        } catch { dynamicVariables = null; }
      }

      let subPages: SubPage[] = [];
      if (template.sub_pages) {
        try {
          subPages = typeof template.sub_pages === 'string'
            ? JSON.parse(template.sub_pages)
            : template.sub_pages;
        } catch { subPages = []; }
      }

      form.setFieldsValue({
        botId: template.bot_id,
        triggerType: template.trigger_type,
        triggerValue: template.trigger_value,
        replyText: template.reply_text,
        replyType: template.reply_type,
        mediaUrl: template.media_url,
        parseMode: template.parse_mode,
        showKeyboard: template.show_keyboard === 1,
        inlineButtons: (() => {
          if (!template.inline_buttons) return [];
          if (Array.isArray(template.inline_buttons)) return template.inline_buttons;
          try { return JSON.parse(template.inline_buttons); } catch { return []; }
        })(),
        dynamicVariables,
        subPages,
        permissionLevel: template.permission_level || 0,
        priority: template.priority,
        status: template.status === 1,
        description: template.description,
      });
      setSubPagesCollapseKey(subPages.length > 0 ? ['subpages'] : []);
    } else {
      setEditingTemplate(null);
      form.resetFields();
      form.setFieldsValue({
        triggerType: 'command',
        replyType: 'text',
        parseMode: 'HTML',
        showKeyboard: false,
        inlineButtons: [],
        dynamicVariables: null,
        subPages: [],
        permissionLevel: 0,
        priority: 0,
        status: true,
      });
      setSubPagesCollapseKey([]);
    }
    setModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        botId: values.botId || null,
        triggerType: values.triggerType,
        triggerValue: values.triggerValue,
        replyText: values.replyText,
        replyType: values.replyType,
        mediaUrl: values.replyType !== 'text' ? values.mediaUrl : null,
        parseMode: values.parseMode,
        showKeyboard: values.showKeyboard ? 1 : 0,
        inlineButtons: values.inlineButtons?.length > 0 ? values.inlineButtons : null,
        dynamicVariables: values.dynamicVariables || null,
        subPages: values.subPages?.length > 0 ? values.subPages : null,
        permissionLevel: values.permissionLevel || 0,
        priority: values.priority,
        status: values.status ? 1 : 0,
        description: values.description,
      };

      if (editingTemplate) {
        await replyTemplateAPI.update(editingTemplate.id, data);
        message.success('模板已更新');
      } else {
        await replyTemplateAPI.create(data);
        message.success('模板已创建');
      }

      setModalVisible(false);
      form.resetFields();
      setSubPagesCollapseKey([]);
      fetchTemplates();
    } catch (error: any) {
      message.error(error.response?.data?.error || '操作失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await replyTemplateAPI.delete(id);
      message.success('模板已删除');
      fetchTemplates();
    } catch (error: any) {
      message.error(error.response?.data?.error || '删除失败');
    }
  };

  const getTriggerPlaceholder = (triggerType: string) => {
    switch (triggerType) {
      case 'command': return '/start 或 /help';
      case 'keyword': return '关键词，如：帮助';
      case 'button': return '按钮文字，如：🚀开始使用';
      default: return '';
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '机器人', dataIndex: 'bot_id', width: 120,
      render: (botId: number | null) => {
        if (botId === null) return <Tag color="purple">全局</Tag>;
        const bot = bots.find(b => b.id === botId);
        return bot ? bot.bot_name : `ID:${botId}`;
      },
    },
    {
      title: '触发类型', dataIndex: 'trigger_type', width: 100,
      render: (type: string) => {
        const map: Record<string, [string, string]> = {
          command: ['blue', '命令'], keyword: ['green', '关键词'], button: ['orange', '按钮'],
        };
        const [color, label] = map[type] || ['default', type];
        return <Tag color={color}>{label}</Tag>;
      },
    },
    { title: '触发值', dataIndex: 'trigger_value', width: 150, ellipsis: true },
    {
      title: '回复内容', dataIndex: 'reply_text', width: 250, ellipsis: true,
      render: (text: string) => <Text ellipsis title={text}>{text}</Text>,
    },
    {
      title: '类型', dataIndex: 'reply_type', width: 80,
      render: (type: string) => {
        const map: Record<string, [string, string]> = {
          text: ['default', '文本'], photo: ['cyan', '图片'], video: ['purple', '视频'],
        };
        const [color, label] = map[type] || ['default', type];
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: '子页面', dataIndex: 'sub_pages', width: 80,
      render: (sub: any) => {
        const pages = Array.isArray(sub) ? sub : (sub ? (() => { try { return JSON.parse(sub); } catch { return []; } })() : []);
        return pages.length > 0 ? <Tag color="geekblue">{pages.length} 页</Tag> : null;
      },
    },
    {
      title: '权限要求', dataIndex: 'permission_level', width: 100,
      render: (level: number) => level === 1
        ? <Tag color="red">仅管理员</Tag>
        : <Tag color="green">所有人</Tag>,
    },
    { title: '优先级', dataIndex: 'priority', width: 80 },
    {
      title: '状态', dataIndex: 'status', width: 80,
      render: (status: number) => status === 1
        ? <Tag icon={<CheckCircleOutlined />} color="success">启用</Tag>
        : <Tag icon={<CloseCircleOutlined />} color="default">禁用</Tag>,
    },
    {
      title: '操作', width: 150, fixed: 'right' as const,
      render: (_: any, record: ReplyTemplate) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => openModal(record)}>编辑</Button>
          <Popconfirm title="确定删除此模板吗？" onConfirm={() => handleDelete(record.id)} okText="确定" cancelText="取消">
            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Title level={3}>私聊回复模板管理</Title>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Select placeholder="按机器人筛选" allowClear style={{ width: '100%' }}
              onChange={value => setFilters({ ...filters, botId: value })}>
              <Select.Option value={null}>全局模板</Select.Option>
              {bots.map(bot => (
                <Select.Option key={bot.id} value={bot.id}>{bot.bot_name}</Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <Select placeholder="按触发类型筛选" allowClear style={{ width: '100%' }}
              onChange={value => setFilters({ ...filters, triggerType: value })}>
              <Select.Option value="command">命令</Select.Option>
              <Select.Option value="keyword">关键词</Select.Option>
              <Select.Option value="button">按钮</Select.Option>
            </Select>
          </Col>
          <Col span={6}>
            <Select placeholder="按状态筛选" allowClear style={{ width: '100%' }}
              onChange={value => setFilters({ ...filters, status: value })}>
              <Select.Option value={1}>启用</Select.Option>
              <Select.Option value={0}>禁用</Select.Option>
            </Select>
          </Col>
        </Row>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchTemplates}>刷新</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>新建模板</Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={templates}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 1400 }}
      />

      <Modal
        title={editingTemplate ? '编辑回复模板' : '新建回复模板'}
        open={modalVisible}
        onCancel={() => { setModalVisible(false); form.resetFields(); setSubPagesCollapseKey([]); }}
        footer={null}
        width={1100}
        style={{ top: 20 }}
        styles={{ body: { maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' } }}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={24}>
            {/* 左侧：表单配置 */}
            <Col span={14}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="机器人" name="botId">
                    <Select placeholder="选择机器人（留空=全局）" allowClear>
                      {bots.map(bot => (
                        <Select.Option key={bot.id} value={bot.id}>{bot.bot_name}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="触发类型" name="triggerType" rules={[{ required: true, message: '请选择触发类型' }]}>
                    <Select>
                      <Select.Option value="command">命令（如 /start）</Select.Option>
                      <Select.Option value="keyword">关键词</Select.Option>
                      <Select.Option value="button">按钮点击</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item noStyle shouldUpdate={(prev, curr) => prev.triggerType !== curr.triggerType}>
                {({ getFieldValue }) => (
                  <Form.Item label="触发值" name="triggerValue" rules={[{ required: true, message: '请输入触发值' }]}>
                    <Input placeholder={getTriggerPlaceholder(getFieldValue('triggerType'))} />
                  </Form.Item>
                )}
              </Form.Item>

              <Alert
                message="支持的内置变量"
                description={
                  <div style={{ fontSize: 12, lineHeight: '22px' }}>
                    <div style={{ marginBottom: 4, fontWeight: 500 }}>用户</div>
                    <div style={{ marginBottom: 6 }}>
                      <Text code>{'{user_id}'}</Text> 用户 Telegram ID &nbsp;
                      <Text code>{'{username}'}</Text> @用户名 &nbsp;
                      <Text code>{'{first_name}'}</Text> 名字 &nbsp;
                      <Text code>{'{last_name}'}</Text> 姓氏 &nbsp;
                      <Text code>{'{start}'}</Text> 深度链接参数（t.me/bot?start=xxx 携带的值）
                    </div>
                    <div style={{ marginBottom: 4, fontWeight: 500 }}>机器人</div>
                    <div>
                      <Text code>{'{bot_id}'}</Text> 机器人 Telegram ID &nbsp;
                      <Text code>{'{bot_username}'}</Text> 机器人 @用户名 &nbsp;
                      <Text code>{'{bot_name}'}</Text> 机器人名称 &nbsp;
                      <Text code>{'{bot_invite_link}'}</Text> 机器人链接
                    </div>
                  </div>
                }
                type="info"
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: 16 }}
              />

              {/* 回复类型 + 媒体 */}
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="回复类型" name="replyType">
                    <Select>
                      <Select.Option value="text">文本</Select.Option>
                      <Select.Option value="photo">图片</Select.Option>
                      <Select.Option value="video">视频</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item noStyle shouldUpdate={(prev, curr) => prev.replyType !== curr.replyType}>
                    {({ getFieldValue }) =>
                      getFieldValue('replyType') !== 'text' ? (
                        <Form.Item label="媒体URL" name="mediaUrl">
                          <Input placeholder="图片/视频的URL地址" />
                        </Form.Item>
                      ) : null
                    }
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item noStyle shouldUpdate={(prev, curr) => prev.replyType !== curr.replyType}>
                {({ getFieldValue }) => {
                  const rt = getFieldValue('replyType');
                  const isMedia = rt !== 'text';
                  return (
                    <Form.Item
                      label={isMedia ? '图片/视频说明文字（Caption）' : '回复内容'}
                      name="replyText"
                      rules={!isMedia ? [{ required: true, message: '请输入回复内容' }] : []}
                    >
                      <TextArea rows={5} placeholder={isMedia ? '显示在图片/视频下方的说明文字（可留空）' : '回复的文本内容，支持变量替换'} />
                    </Form.Item>
                  );
                }}
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="解析模式" name="parseMode">
                    <Select>
                      <Select.Option value="HTML">HTML</Select.Option>
                      <Select.Option value="Markdown">Markdown</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="显示自定义键盘" name="showKeyboard" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>

              {/* 动态变量 */}
              <Form.Item label="动态变量（外部API）" name="dynamicVariables">
                <DynamicVariableEditor />
              </Form.Item>

              {/* Inline 按钮（主页面） */}
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  const dvConfig = getFieldValue('dynamicVariables');
                  const dvFields = dvConfig?.response_fields?.filter((f: string) => f.trim()) || [];
                  const authCodeNames = (dvConfig?.auth_codes || [])
                    .map((c: any) => c.variable_name)
                    .filter(Boolean);
                  const subPagesVal: SubPage[] = getFieldValue('subPages') || [];
                  const allPageOptions = [
                    { pageIndex: 0, title: '主页面' },
                    ...subPagesVal.map(p => ({ pageIndex: p.pageIndex, title: p.title || `子页面 ${p.pageIndex}` })),
                  ];
                  return (
                    <Form.Item label="Inline 按钮（主页面）" name="inlineButtons">
                      <InlineButtonEditor
                        dynamicVarFields={dvFields}
                        authCodeVarNames={authCodeNames}
                        availablePages={allPageOptions}
                        currentPageIndex={0}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>

              {/* 子页面 */}
              <Collapse
                style={{ marginBottom: 16 }}
                activeKey={subPagesCollapseKey}
                onChange={(keys) => setSubPagesCollapseKey(typeof keys === 'string' ? [keys] : keys as string[])}
                items={[{
                  key: 'subpages',
                  label: (
                    <Space>
                      <Text strong>子页面（多级菜单）</Text>
                      <Form.Item noStyle shouldUpdate={(p, c) => p.subPages !== c.subPages}>
                        {({ getFieldValue }) => {
                          const cnt = (getFieldValue('subPages') || []).length;
                          return <Text type="secondary" style={{ fontSize: 12 }}>
                            {cnt > 0 ? `${cnt} 个子页面` : '未配置'}
                          </Text>;
                        }}
                      </Form.Item>
                    </Space>
                  ),
                  children: (
                    <Form.Item noStyle shouldUpdate>
                      {({ getFieldValue }) => {
                        const dvConfig = getFieldValue('dynamicVariables');
                        const dvFields = dvConfig?.response_fields?.filter((f: string) => f.trim()) || [];
                        const authCodeNames = (dvConfig?.auth_codes || [])
                          .map((c: any) => c.variable_name)
                          .filter(Boolean);
                        return (
                          <Form.Item name="subPages" noStyle>
                            <SubPagesEditor dynamicVarFields={[...dvFields, ...authCodeNames]} />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  ),
                }]}
              />

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="触发权限" name="permissionLevel">
                    <Select>
                      <Select.Option value={0}>所有人</Select.Option>
                      <Select.Option value={1}>仅管理员</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="优先级" name="priority">
                    <InputNumber style={{ width: '100%' }} placeholder="数值越大优先级越高" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="启用" name="status" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="描述" name="description">
                <Input placeholder="模板说明（可选）" />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit">
                    {editingTemplate ? '更新' : '创建'}
                  </Button>
                  <Button onClick={() => setModalVisible(false)}>取消</Button>
                </Space>
              </Form.Item>
            </Col>

            {/* 右侧：实时预览 */}
            <Col span={10}>
              <div style={{ position: 'sticky', top: 0 }}>
                <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
                  实时预览
                </Text>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => {
                    const dvConfig = getFieldValue('dynamicVariables');
                    const dvFields = dvConfig?.response_fields?.filter((f: string) => f.trim()) || [];
                    const subPages: SubPage[] = getFieldValue('subPages') || [];
                    return (
                      <TelegramChatPreview
                        triggerText={getFieldValue('triggerValue')}
                        replyText={getFieldValue('replyText')}
                        replyType={getFieldValue('replyType') || 'text'}
                        mediaUrl={getFieldValue('mediaUrl')}
                        parseMode={getFieldValue('parseMode') || 'HTML'}
                        buttons={getFieldValue('inlineButtons') || []}
                        dynamicVarFields={dvFields}
                        subPages={subPages}
                      />
                    );
                  }}
                </Form.Item>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default PrivateReplyTemplates;
